import { Component } from '@angular/core';
import { ElementRef, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { UserService } from '../../services/user.service';
import { SearchService } from '../../search/services/search.service';
import dayjs from 'dayjs';
import flatpickr from 'flatpickr';

// For typeahead functionality
import { Observable, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

/**
 * Component providing implementation of a system search field.
 * Migrated from srsField.directive.js
 */
@Component({
	selector: 'srs-field',
	templateUrl: './srs-field.component.html',
	styleUrl: './srs-field.component.css'
})
export class SrsFieldComponent implements OnInit, OnDestroy {
	@Input() field: any;
	@Output() triggerUpdateSearch = new EventEmitter<void>();

	private userEventSubscription!: Subscription;
	private searchEventSubscription!: Subscription;

	LOCALIZATION: any;

	draggedOver = false;
	isValid = true;
	dateFormatDisplay: string = 'MM/DD/YY';

	constructor(
		private userService: UserService,
		private searchService: SearchService,
		private elementRef: ElementRef
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
	}

	ngOnInit() {
		let dateTypeProperty = this.userService.user.properties.awpropertyObjectList.find((x: any) => x.name == 'USER_INTERFACE_DATE_TYPE');
		this.dateFormatDisplay = dateTypeProperty?.value || 'MM/DD/YY';

		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if (event === 'localizationLoaded') {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			}			
		});
				
		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe(event => {
			if (event.type === 'clearSearch') {
				this.isValid = true;
				this.field.filter = null;
			}
			else if (event.type === 'dragTarget') {
				// Drag has ended. Clear the drag target state for all fields.
				this.resetDrag();
			}
		});
	}
	
	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}
		if (this.searchEventSubscription) {
			this.searchEventSubscription.unsubscribe();
		}		
	}

	updateSearch(): void {
		this.triggerUpdateSearch.emit();
	}

	blur(event: any): void {
		this.validate(event);
	}

	dateValidation(event: any): void {
		this.validate(event);
		if (this.isValid) {
			this.updateSearch();
		}
	}

	validate(event: any) {
		this.isValid = true;
		let val = event.target.value;
		if (!val || val.trim() == "") {
			return;
		}
		let dates = val.split(' - ');
		if (dates.length == 1) {
			this.isValid = dayjs(dates[0], this.dateFormatDisplay).isValid();
		} else if (dates.length == 2) {
			if (!dayjs(dates[0], this.dateFormatDisplay).isValid()) {
				this.isValid = false;
			}
			if (!dayjs(dates[1], this.dateFormatDisplay).isValid()) {
				this.isValid = false;
			}
		} else {
			this.isValid = false;
		}
	}

	removeValues(formItem: any) {
		formItem.content = formItem.type === 'STRING' ? [{ content: null }] : null;
	}

	clearSearch(index: number) {
		if (index >= 0) {
			this.field.list[index].content = '';
		} else {
			this.field.content = '';
		}
	}

	updateFn(formItem: any) {
		// This function is implemented in fields.service.ts
		formItem.updateFn(formItem, false);
	}

	// Converts the service Promise to an Observable emitting a string array
	private getSuggestions(fieldid: string, val: string): Observable<string[]> {
		// searchService returns a Promise
		return from(this.searchService.getTermSuggestions(fieldid, val)) as Observable<string[]>;
	}

	/**
	 * Typeahead search handler.
	 */
	search = (text$: Observable<string>) =>
		text$.pipe(
			debounceTime(200),
			distinctUntilChanged(),
			switchMap((term) => {
				const fieldName = this.field.name;

				console.log('Received term:', term, 'for field:', fieldName);

				return from(this.getSuggestions(fieldName, term));
			})
		);

	/**
	 * Field drag / drop handler.
	 */
	dragActions(msg: string, event: DragEvent): void {
		event.preventDefault();
		
		if (this.field.draggable) {
			if (msg === 'enter' && !this.draggedOver) {
				this.searchService.broadcastEvent.emit({type:'dragDestination', data : { destination: 'SearchCtrlFilter', object: this.field }});
			}
			else if (msg === 'leave') {
				this.searchService.broadcastEvent.emit({type:'dragLeaveDestination', data : { destination: 'SearchCtrlFilter', object: this.field }});
			}
			else if (msg === 'end') {
				this.searchService.broadcastEvent.emit({type:'dragTarget', data : { destination: 'SearchCtrlFilter', object: this.field }});
			}
			
			this.draggedOver = msg === 'enter';
		}
	}
	
	resetDrag(): void {
		this.draggedOver = false;
	}

	/**
	 * Opens a date picker for the field.
	 */
	openDatePicker(fieldName: string): void {
		let target = this.elementRef.nativeElement.querySelector(
		  `[data-field="${fieldName}"]`
		);

		if (!target) return;

		if (!target._flatpickr) {
			let opts = this.userService.getDatePickerOptions(undefined, undefined, null, fieldName);
			opts.mode = "range";
			let isToday = dayjs().isSame(opts.defaultDate, 'day');
			if (isToday) delete opts.defaultDate;
			let datepicker = flatpickr(target, opts);
			this.field.datepicker = datepicker;
		}
		if (target._flatpickr.isOpen) {
			target._flatpickr.close();
		} else {
			target._flatpickr.open();
		}
	}
}
